/**
 * Locale-aware formatting for quantities and percentages shown in the UI.
 *
 * - **Quantities / generic decimals** (counts, BOM amounts, liters when not monetary):
 *   use {@link formatDecimal}. Do not use for money — use {@link formatCurrency} /
 *   {@link formatCurrencyCompact} from `./currency.ts`.
 * - **Percentages for display** (margins, ownership, KPI rates already expressed as %):
 *   use {@link formatPercentDisplay}. Financial calculations stay on the backend; this module
 *   only affects presentation.
 *
 * Locale matches {@link getAppNumberLocale} in `./appNumberLocale.ts`: Arabic → `ar-DZ`,
 * otherwise `fr-DZ`, keyed off the current `i18n.language`.
 */
import { getAppNumberLocale } from "./appNumberLocale";
import {
  DECIMAL_PLACEHOLDER,
  type FractionDigitOptions,
  isDisplayableNumber,
  resolveIntlFractionDigits,
} from "./numberFormatPrimitives";

export interface FormatDecimalOptions extends FractionDigitOptions {
  /**
   * When omitted, fractional part shows **at most 2** digits (epic default).
   * When set, values above 2 are honored for documented exceptions (capped at 20).
   */
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export interface FormatPercentDisplayOptions extends FractionDigitOptions {
  /**
   * - `percent` (default): value is on a **0-100** scale (e.g. `45.6` displays as `45.6%`).
   * - `ratio`: value is on a **0-1** scale (e.g. `0.456` displays as `45.6%`).
   */
  inputScale?: "percent" | "ratio";
  /**
   * Same rules as {@link FormatDecimalOptions.minimumFractionDigits}.
   */
  minimumFractionDigits?: number;
  /**
   * Same rules as {@link FormatDecimalOptions.maximumFractionDigits}.
   */
  maximumFractionDigits?: number;
}

/** Arabic percent sign (U+066A); Latin `%` is used for non-Arabic locales. */
const ARABIC_PERCENT = "\u066a";

function percentSuffixForLocale(locale: string): string {
  return locale.startsWith("ar") ? ARABIC_PERCENT : "%";
}

/**
 * Whether a spend-delta headline should prepend an explicit `+` (card headline style).
 * Excludes negatives and −0 (in JS, `-0 >= 0` is true, so `pct >= 0` is not enough).
 */
export function pctDeltaShowsExplicitPlus(pct: number): boolean {
  return !(pct < 0 || Object.is(pct, -0));
}

/**
 * Format a non-currency numeric quantity using `Intl.NumberFormat`.
 * By default fractional digits are **at most 2**; pass `maximumFractionDigits` to allow more
 * (see {@link FormatDecimalOptions}).
 */
export function formatDecimal(
  value: number | string | null | undefined,
  options?: FormatDecimalOptions,
): string {
  if (!isDisplayableNumber(value)) return DECIMAL_PLACEHOLDER;
  const n = Number(value);
  const locale = getAppNumberLocale();
  const { min, max } = resolveIntlFractionDigits(options);
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(n);
}

/**
 * Format a numeric percentage for display (locale-appropriate percent marker, locale-aware separators).
 * Default input is **0–100** (see {@link FormatPercentDisplayOptions.inputScale}); by default **at most 2**
 * fractional digits unless you set `maximumFractionDigits`.
 */
export function formatPercentDisplay(
  value: number | string | null | undefined,
  options?: FormatPercentDisplayOptions,
): string {
  if (!isDisplayableNumber(value)) return DECIMAL_PLACEHOLDER;
  const n = Number(value);
  const scale = options?.inputScale ?? "percent";
  const pct = scale === "ratio" ? n * 100 : n;
  const locale = getAppNumberLocale();
  const { min, max } = resolveIntlFractionDigits(options);
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(pct);
  return `${formatted}${percentSuffixForLocale(locale)}`;
}
